import unifiedLoggerClient from '@modules/eventStream/implementations/unifiedLoggerClient';
import { Grid, Alert } from '@rbx/ui';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import usePageLogs from '@modules/miscellaneous/common/hooks/usePageLogs';
import LoggedPage from '@modules/eventStream/enum/LoggedPage';
import DocsAssistantEvent from '@modules/eventStream/enum/DocsAssistantEvent';
import { useLocalization, useTranslation } from '@rbx/intl';
import {
  ConnectionState,
  ConversationState,
  MessageHistoryItem,
  useProvidedConversationReducer,
} from '@rbx/conv-ai-provider';
import { CreateConversationResponse } from '@rbx/clients/convAiPlatformOrchestrationService';
import { createConversation } from '@modules/clients/convAi';
import { NextRouter, useRouter } from 'next/router';
import { useAssistantOptIn, OptInState } from '../context/AssistantOptInProvider';
import { useIsAuthenticated } from '../context/IsAuthenticatedProvider';
import useAssistantStyles from './Assistant.styles';
import NullState from './NullState';
import AssistantInput from './AssistantInput';
import ConversationStream from './ConversationStream';
import LoginRequiredDialog from './LoginRequiredDialog';
import useBotAlertStyles from './BotAlert.styles';
import { useSentPrompts } from '../hooks/useSendToAssistant';
import { PROMPT_ID_QUERY_PARAM } from './LinkToAssistant';

enum ExamplePromptCategory {
  Monetize = 'Monetize',
  Build = 'Build',
  Grow = 'Grow',
}

const clearPromptIdQueryParam = (router: NextRouter) => {
  const { [PROMPT_ID_QUERY_PARAM]: _, ...restQuery } = router.query;
  router.replace({ pathname: router.pathname, query: { ...restQuery } });
};

type TAssistantProps = {
  enableIAM2?: boolean;
  scrollToBottom?: VoidFunction;
};
// TODO (@ahua, CRK-2919): UI needs translation strings
const Assistant: React.FC<TAssistantProps> = ({
  enableIAM2 = false,
  scrollToBottom: scrollToBottomIA,
}) => {
  const { locale } = useLocalization();
  const { translate } = useTranslation();
  const {
    isPageRefresh,
    isReady: isPageLogsReady,
    lastLoggedEntryPoint,
  } = usePageLogs(LoggedPage.Assistant);
  const { isAuthLoaded, isAuthenticated } = useIsAuthenticated();
  const {
    classes: { alert },
  } = useBotAlertStyles();
  // while isAuthLoaded is false, LoginRequiredDialog
  // will not appear (yet), but starting chat is disabled
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [containerHeight, setContainerHeight] = useState(0);
  const [inputHeight, setInputHeight] = useState(0);
  const {
    classes: { container, wrapper },
  } = useAssistantStyles();

  const { sentPrompts, clearSentPrompt } = useSentPrompts();
  const [sentPromptId, setSentPromptId] = useState<string | null>(null);

  const promptAssistantResponse = useCallback(
    async (state: ConversationState): Promise<CreateConversationResponse> => {
      if (!state.activePromptMessage) throw new Error('No active prompt message');
      return createConversation({
        conversationId: state.conversationId,
        messageHistory: state.messageHistory as MessageHistoryItem[],
        messageToSend: state.activePromptMessage,
      });
    },
    [],
  );

  const {
    state: {
      messageHistory,
      activeRequestId,
      activeResponseMessage,
      activePromptMessage,
      streaming,
      connectionState,
    },
    actions: { sendUserPrompt, stopResponse },
  } = useProvidedConversationReducer();
  const {
    initialize: initializeOptIn,
    sentPromptPending: sentPromptPendingOptIn,
    processing: processingOptIn,
    state: optInState,
    accepted: acceptedOptIn,
  } = useAssistantOptIn();
  const onSendUserPrompt = useCallback(
    (userPrompt: string) => {
      if (!isAuthenticated) return;
      sendUserPrompt({ userPrompt, promptAssistantResponse });
    },
    [isAuthenticated, sendUserPrompt, promptAssistantResponse],
  );
  const onSendExamplePrompt = useCallback(
    (userPrompt: string, examplePromptCategory: ExamplePromptCategory) => {
      if (!isAuthenticated) return;
      sendUserPrompt({ userPrompt, examplePromptCategory, promptAssistantResponse });
    },
    [isAuthenticated, sendUserPrompt, promptAssistantResponse],
  );

  const onInputClientHeightChange = (height: number, containerHt: number) => {
    setInputHeight(Math.ceil(height));
    setContainerHeight(containerHt);
  };
  const scrollToBottom = () => {
    if (scrollToBottomIA) {
      scrollToBottomIA();
    }
    if (containerRef?.current) containerRef.current.scrollTop = containerRef.current.scrollHeight;
  };
  const notLoggedIn = useMemo(
    () => isAuthLoaded && !isAuthenticated,
    [isAuthLoaded, isAuthenticated],
  );

  useEffect(scrollToBottom, [
    containerRef,
    messageHistory,
    activeResponseMessage,
    activeRequestId,
    activePromptMessage,
    streaming,
    scrollToBottomIA,
  ]);

  useEffect(() => {
    if (notLoggedIn) {
      unifiedLoggerClient.logImpressionEvent({
        eventName: DocsAssistantEvent.LoginRequiredDialog,
        parameters: {
          locale: locale ? String(locale) : '',
        },
      });
    }
  }, [locale, notLoggedIn]);

  useEffect(() => {
    if (!isPageLogsReady) return;
    unifiedLoggerClient.logImpressionEvent({
      eventName: DocsAssistantEvent.EntryPoint,
      parameters: {
        source: String(lastLoggedEntryPoint),
        locale: locale ? String(locale) : '',
      },
    });
  }, [locale, isPageRefresh, isPageLogsReady, lastLoggedEntryPoint]);

  useEffect(() => {
    unifiedLoggerClient.trackPageLoad();
    initializeOptIn();
  }, [initializeOptIn]);

  const initiatedChat = useMemo(
    () => activePromptMessage !== undefined || processingOptIn,
    [activePromptMessage, processingOptIn],
  );

  const isReadyToSendPrompt = useMemo(
    () => acceptedOptIn && isAuthenticated && connectionState === ConnectionState.Connected,
    [acceptedOptIn, isAuthenticated, connectionState],
  );

  const router = useRouter();

  useEffect(() => {
    if (!router.query[PROMPT_ID_QUERY_PARAM]) return;

    const { [PROMPT_ID_QUERY_PARAM]: idParam } = router.query;

    const id = Array.isArray(idParam) ? idParam[0] : idParam;

    if (id && sentPrompts[id]) {
      setSentPromptId(id);
    } else {
      // invalid prompt id, remove from query param
      clearPromptIdQueryParam(router);
    }
  }, [router, router.query, sentPrompts, setSentPromptId]);

  useEffect(() => {
    if (optInState === OptInState.Initialized && sentPromptId !== null) {
      sentPromptPendingOptIn();
    }
  }, [optInState, sentPromptPendingOptIn, sentPromptId]);

  useEffect(() => {
    if (!sentPromptId || !isReadyToSendPrompt) return;
    if (!sentPrompts[sentPromptId]) {
      // prompt id is not in sentPrompts, remove from query param
      setSentPromptId(null);
      clearPromptIdQueryParam(router);
    } else {
      onSendUserPrompt(sentPrompts[sentPromptId]);
      clearSentPrompt(sentPromptId);
      setSentPromptId(null);
      clearPromptIdQueryParam(router);
    }
  }, [
    sentPromptId,
    isReadyToSendPrompt,
    sentPrompts,
    setSentPromptId,
    router,
    onSendUserPrompt,
    clearSentPrompt,
    optInState,
  ]);

  const content = useMemo(() => {
    if (optInState === OptInState.Preinitialized) {
      return null;
    }
    if (optInState === OptInState.FailedToInitialize) {
      return (
        <Alert aria-label={translate('Label.Alert')} className={alert} severity='error'>
          {translate('AlertMessage.Generic')}
        </Alert>
      );
    }
    return initiatedChat ? (
      <div>
        <ConversationStream />
      </div>
    ) : (
      <NullState onSendExamplePrompt={onSendExamplePrompt} />
    );
  }, [alert, initiatedChat, onSendExamplePrompt, optInState, translate]);

  const chatHeight = inputHeight && containerHeight - inputHeight;
  if (!isAuthLoaded) {
    return null;
  }
  if (notLoggedIn) {
    return <LoginRequiredDialog />;
  }

  const chatContent = enableIAM2 ? (
    <Grid
      container
      className={wrapper}
      ref={containerRef}
      style={{
        marginBottom: inputHeight,
      }}>
      {content}
    </Grid>
  ) : (
    <Grid
      container
      className={container}
      ref={containerRef}
      style={{
        height: chatHeight,
      }}>
      <div className={wrapper} style={{ minHeight: chatHeight }}>
        {content}
      </div>
    </Grid>
  );

  return (
    <React.Fragment>
      {chatContent}
      <AssistantInput
        onClientHeightChange={onInputClientHeightChange}
        onSendPrompt={onSendUserPrompt}
        onStopResponse={stopResponse}
        streaming={streaming}
      />
    </React.Fragment>
  );
};

export default Assistant;
